import { EventRecord, EventService } from "@/shared/domain/events";
import { tryBuildEventScopeLayout } from "@/shared/domain/events/eventScopeLayout";
import { eventQueryKeys } from "@/shared/domain/events/hooks";
import {
  EventScope,
  resolveEventScope,
} from "@/shared/domain/events/resolveEventScope";
import { TopicService } from "@/shared/domain/topics";
import { RobotoNotFoundException } from "@/shared/http";
import { RobotoUnauthorizedException } from "@/shared/http/exceptions";
import { Datasource } from "@/shared/state/visualization";

import { temporaryLayoutRecordFrom } from "./resolvers";
import {
  type ConfigSource,
  type EventSidebar,
  type VisualizerInitContext,
  type VisualizerNotice,
} from "./visualizerInit";

/**
 * The result of resolving the URL's `?event=`: the event record and the
 * scope its associations cover when the event loaded, or a marker for the
 * two ways loading it can fail that the view recovers from.
 */
export type EventResolution =
  | { status: "ok"; event: EventRecord; scope: EventScope }
  | { status: "not-found"; eventId: string }
  | { status: "unauthorized"; eventId: string };

/**
 * Fetch the event named in the URL and resolve its scope in one pass, so the
 * datasources, panels, sidebar, and notices it feeds all read from a single
 * fetch. Returns null when the URL names no event.
 *
 * A missing or forbidden event returns a marker rather than throwing: losing
 * the linked event must not tear down a workspace whose URL datasources the
 * user may be allowed to see. Unexpected failures still throw for the loader
 * to log.
 *
 * @throws Error when the event fetch fails for any reason other than a 404 or 403
 */
export async function resolveEvent(
  ctx: VisualizerInitContext,
): Promise<EventResolution | null> {
  const { eventId, orgId } = ctx;

  if (!eventId) {
    return null;
  }

  const eventService = new EventService(ctx.httpClient);

  let event: EventRecord;
  try {
    // Keyed so the workspace sidebar's event-details view finds the event
    // already cached.
    event = await ctx.queryClient.ensureQueryData({
      queryKey: eventQueryKeys.detail(orgId, eventId),
      queryFn: ({ signal }) =>
        eventService.getEventById(eventId, { abortSignal: signal }),
    });
  } catch (error) {
    if (error instanceof RobotoNotFoundException) {
      return { status: "not-found", eventId };
    }
    if (error instanceof RobotoUnauthorizedException) {
      return { status: "unauthorized", eventId };
    }

    throw new Error(`Failed to resolve event ${eventId}`, { cause: error });
  }

  const topicService = new TopicService(ctx.httpClient);
  const scope = await resolveEventScope(event, topicService);

  return { status: "ok", event, scope };
}

/** The event's id, whichever way it resolved. */
export function eventIdOf(resolution: EventResolution): string {
  return resolution.status === "ok"
    ? resolution.event.event_id
    : resolution.eventId;
}

/**
 * A layout config source built from the event's message-path scope, or null
 * when the event failed to load or its scope produces no panels. The event
 * supplies panels only when no higher-priority source does.
 */
export function resolveEventPanels(
  resolution: EventResolution | null,
): ConfigSource | null {
  if (resolution?.status !== "ok") {
    return null;
  }

  const layout = tryBuildEventScopeLayout(resolution.scope.scopedMessagePaths);
  if (!layout) {
    return null;
  }

  return {
    type: "layout",
    value: temporaryLayoutRecordFrom(layout),
    origin: "event",
  };
}

/**
 * The datasources the event's associations resolve to, ready to merge with
 * the URL's own. Empty when the event failed to load.
 */
export function eventDatasources(
  resolution: EventResolution | null,
): Datasource[] {
  if (resolution?.status !== "ok") {
    return [];
  }

  const { datasetIds, fileIds } = resolution.scope;
  return [
    ...datasetIds.map((datasetId) => ({
      type: "dataset" as const,
      value: { datasetId },
    })),
    ...fileIds.map((fileId) => ({
      type: "file" as const,
      value: { fileId },
    })),
  ];
}

/**
 * Which sidebar view the `?event=` link opens: the event's details when it
 * loaded, the events list when it could not (so the user is not stuck on a
 * broken detail view), or nothing when the URL named no event.
 */
export function eventSidebarFor(
  resolution: EventResolution | null,
): EventSidebar | undefined {
  if (!resolution) {
    return undefined;
  }

  if (resolution.status === "ok") {
    return { eventId: resolution.event.event_id, view: "details" };
  }

  return { eventId: resolution.eventId, view: "events-list" };
}

/**
 * Banners raised for the event's own state: not found, forbidden, or loaded
 * but with no reachable associated data. Empty when the event loaded with
 * data or the URL named no event.
 */
export function eventNotices(
  resolution: EventResolution | null,
): VisualizerNotice[] {
  if (!resolution) {
    return [];
  }

  if (resolution.status === "not-found") {
    return [
      {
        name: "EventNotFoundError",
        message: `Event '${resolution.eventId}' not found.`,
        severity: "error",
      },
    ];
  }

  if (resolution.status === "unauthorized") {
    // Access to an event comes from its datasets and files — events can't be
    // shared individually — so the remedy names those, not the event.
    return [
      {
        name: "EventUnauthorizedError",
        title: "Unauthorized",
        message: `Viewing event '${resolution.eventId}' requires access to its underlying data.`,
        severity: "error",
      },
    ];
  }

  const { datasetIds, fileIds } = resolution.scope;
  if (datasetIds.length === 0 && fileIds.length === 0) {
    return [
      {
        name: "EventScopeUnresolvedError",
        message: `Data underlying event '${resolution.event.event_id}' not found.`,
        severity: "error",
      },
    ];
  }

  return [];
}
